import {
  Button,
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  LOCK_WARNING,
  ListContext,
  ListContextValue,
  LockedIcon,
  MenuItemType,
  Stack,
  Switch,
  Tooltip,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, Permission, PermissionAction, StaffService } from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const StaffServicePageHeader = (props: {
  theme?: "Dark" | "Standard";
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  service: StaffService;
  setService: (service: StaffService) => void;
  initService: StaffService;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
  resetUnsavedChanges: (items: any[]) => void;
  hideStaff: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.service.id;
  const [staffName, setStaffName] = useState<string>("");
  const [createAnother, setCreateAnother] = useState<boolean>(false);
  const [createAnotherRetainData, setCreateAnotherRetainData] = useState<boolean>(false);

  useEffect(() => {
    if (props.service.id === 0 && props.service.staffId > 0) {
      ApiService.staffServices.getStaffName(props.service.staffId).then((response) => {
        setStaffName(response);
      });
    }
  }, [props.service.staffId]);

  const duplicate = () => {
    ApiService.staffServices.duplicate(id).then((response) => {
      props.setService(response);
      props.resetUnsavedChanges([response]);
      props.reroute(0, "edit");
    });
  };

  const save = (service: StaffService) => {
    props.setValidationErrors({});

    if (service.id > 0) {
      ApiService.staffServices
        .saveUpdate(service)
        .then(() => {
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    } else {
      ApiService.staffServices
        .saveCreate(service)
        .then((response) => {
          const id = response;
          if (createAnother) {
            if (createAnotherRetainData) {
              props.resetUnsavedChanges([service]);
            } else {
              props.setService(props.initService);
              props.resetUnsavedChanges([props.initService]);
            }
          } else {
            props.reroute(id, "view");
            props.fetch(id);
          }
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.staffServices.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (
      isAuthorized(props.rootPermission, PermissionAction.Delete) &&
      (!props.service.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock))
    )
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Stack>
          <Typography variant="inherit">
            {id === 0 ? "New" : ""} {props.handle}
          </Typography>

          {id === 0 && props.hideStaff && (
            <Typography variant="caption" lineHeight={"100%"}>
              for {staffName}
            </Typography>
          )}
        </Stack>

        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.service.isLocked && <LockedIcon />}

        <Stack>
          {id === 0 && (
            <>
              <Switch
                value={createAnother}
                onChange={(e) => setCreateAnother(e.target.checked)}
                label="+ more"
                size={createAnother ? "small" : undefined}
              />

              {createAnother && (
                <Tooltip title="Retain data after add">
                  <Switch
                    value={createAnotherRetainData}
                    onChange={(e) => setCreateAnotherRetainData(e.target.checked)}
                    label="Retain"
                    size="small"
                  />
                </Tooltip>
              )}
            </>
          )}
        </Stack>

        {id > 0 && <DataModelInformation dataModelType={DataModelEnum.StaffService} dataModelId={id} />}

        <CloseButton theme={props.theme} confirm={props.unsavedChanges} />

        {id > 0 && props.crudMode === "view" && (
          <Button variant="outlined" onClick={duplicate} color="info">
            Duplicate
          </Button>
        )}

        {props.crudMode === "view" &&
          isAuthorized(props.rootPermission, PermissionAction.Update) &&
          (!props.service.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
            <UpdateButton theme={props.theme} onClick={() => props.reroute(id, "edit")} />
          )}

        {props.crudMode === "edit" && (
          <SubmitButton theme={props.theme} onClick={() => save(props.service)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
